/*
  This example requires some changes to your config:

  ```
  // tailwind.config.js
  module.exports = {
    // ...
    plugins: [
      // ...
      require('@tailwindcss/forms'),
    ]
  }
  ```
*/
import React from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";
import MediaWrapper from "@/components/mediaWrapper";
import MarkdownWrapper from "@/components/markdownWrapper";

export const query = graphql`
  fragment HeroWithSignUpAndMediaContentData on StrapiComponentHeroWithSignUpAndMediaContent {
    title
    topic
    description
    signUpText
    signUpPlaceholderText
    signUpButtonText
    privacyPolicyText
    mediaContent {
      ...StrapiMediaSubquery
    }
  }
`;

function WithSignUpAndMediaContent({ data }) {
  if (!data) {
    return <template />;
  }
  return (
    <div className="relative overflow-hidden bg-background-white">
      <div
        className="hidden lg:absolute lg:inset-0 lg:block"
        aria-hidden="true"
      >
        <svg
          className="absolute left-1/2 top-0 -translate-y-8 translate-x-64 transform"
          width={640}
          height={784}
          fill="none"
          viewBox="0 0 640 784"
        >
          <defs>
            <pattern
              id="9ebea6f4-a1f5-4d96-8c4e-4c2abf658047"
              x={118}
              y={0}
              width={20}
              height={20}
              patternUnits="userSpaceOnUse"
            >
              <rect
                x={0}
                y={0}
                width={4}
                height={4}
                className="text-text-light-gray"
                fill="currentColor"
              />
            </pattern>
          </defs>
          <rect
            y={72}
            width={640}
            height={640}
            className="text-text-off-white"
            fill="currentColor"
          />
          <rect
            x={118}
            width={404}
            height={784}
            fill="url(#9ebea6f4-a1f5-4d96-8c4e-4c2abf658047)"
          />
        </svg>
      </div>

      <div className="relative pb-16 pt-6 sm:pb-24 lg:pb-32">
        <main className="mx-auto mt-16 max-w-7xl px-4 sm:mt-24 sm:px-6 lg:mt-32">
          <div className="lg:grid lg:grid-cols-12 lg:gap-8">
            <div className="sm:text-center md:mx-auto md:max-w-2xl lg:col-span-6 lg:text-left">
              <h1>
                {data.topic && (
                  <span className="block text-sm font-semibold uppercase tracking-wide text-text-secondary sm:text-base lg:text-sm xl:text-base">
                    <MarkdownWrapper text={data.topic} />
                  </span>
                )}
                {data.title && (
                  <span className="mt-1 block text-4xl font-extrabold tracking-tight sm:text-5xl xl:text-6xl">
                    <span className="block text-text-primary">
                      <MarkdownWrapper text={data.title} />
                    </span>
                  </span>
                )}
              </h1>
              {data.description && (
                <MarkdownWrapper
                  text={data.description}
                  markdownClassName="mt-3 text-base text-text-secondary sm:mt-5 sm:text-xl lg:text-lg xl:text-xl"
                  replaceParagraph={false}
                />
              )}
              <div className="mt-8 sm:mx-auto sm:max-w-lg sm:text-center lg:mx-0 lg:text-left">
                {data.signUpText && (
                  <MarkdownWrapper
                    text={data.signUpText}
                    markdownClassName="text-base font-medium text-text-primary"
                  />
                )}
                <form action="#" method="POST" className="mt-3 sm:flex">
                  <input
                    type="text"
                    name="email"
                    id="email"
                    className="block w-full rounded-md border-border-secondary-light py-3 text-base placeholder-placeholder-gray shadow-sm focus:border-border-primary focus:ring-ring-primary sm:flex-1"
                    placeholder={data.signUpPlaceholderText || "Email"}
                  />
                  <button
                    type="submit"
                    className="mt-3 w-full rounded-md border border-transparent bg-background-primary px-6 py-3 text-base font-medium text-text-white shadow-sm hover:bg-background-primary-dark focus:outline-none focus:ring-2 focus:ring-ring-primary focus:ring-offset-2 sm:ml-3 sm:mt-0 sm:inline-flex sm:w-auto sm:flex-shrink-0 sm:items-center"
                  >
                    {data.signUpButtonText || "Submit"}
                  </button>
                </form>
                {data.privacyPolicyText && (
                  <MarkdownWrapper
                    text={data.privacyPolicyText}
                    markdownClassName="mt-3 text-sm text-text-secondary"
                    replaceParagraph={false}
                  />
                )}
              </div>
            </div>
            <div className="relative mt-12 sm:mx-auto sm:max-w-lg lg:col-span-6 lg:mx-0 lg:mt-0 lg:flex lg:max-w-none lg:items-center">
              <svg
                className="absolute left-1/2 top-0 origin-top -translate-x-1/2 -translate-y-8 scale-75 transform sm:scale-100 lg:hidden"
                width={640}
                height={784}
                fill="none"
                viewBox="0 0 640 784"
                aria-hidden="true"
              >
                <defs>
                  <pattern
                    id="4f4f415c-a0e9-44c2-9601-6ded5a34a13e"
                    x={118}
                    y={0}
                    width={20}
                    height={20}
                    patternUnits="userSpaceOnUse"
                  >
                    <rect
                      x={0}
                      y={0}
                      width={4}
                      height={4}
                      className="text-text-light-gray"
                      fill="currentColor"
                    />
                  </pattern>
                </defs>
                <rect
                  y={72}
                  width={640}
                  height={640}
                  className="text-text-off-white"
                  fill="currentColor"
                />
                <rect
                  x={118}
                  width={404}
                  height={784}
                  fill="url(#4f4f415c-a0e9-44c2-9601-6ded5a34a13e)"
                />
              </svg>
              <div className="relative mx-auto w-full rounded-lg shadow-lg lg:max-w-md">
                <button
                  type="button"
                  className="relative block w-full overflow-hidden rounded-lg bg-background-white focus:outline-none focus:ring-2 focus:ring-ring-primary focus:ring-offset-2"
                >
                  {/*<span className="sr-only">Watch our video to learn more</span>*/}
                  {data.mediaContent && (
                    <MediaWrapper
                      media={data.mediaContent}
                      divClassName="w-full"
                    />
                  )}
                  <div
                    className="absolute inset-0 flex h-full w-full items-center justify-center"
                    aria-hidden="true"
                  >
                    <svg
                      className="h-20 w-20 text-text-primary"
                      fill="currentColor"
                      viewBox="0 0 84 84"
                    >
                      <circle
                        opacity="0.9"
                        cx={42}
                        cy={42}
                        r={42}
                        fill="white"
                      />
                      <path d="M55.5039 40.3359L37.1094 28.0729C35.7803 27.1869 34 28.1396 34 29.737V54.263C34 55.8604 35.7803 56.8131 37.1094 55.9271L55.5038 43.6641C56.6913 42.8725 56.6913 41.1275 55.5039 40.3359Z" />
                    </svg>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

WithSignUpAndMediaContent.propTypes = {
  data: PropTypes.object,
};

export default WithSignUpAndMediaContent;
